import { Language } from '../data/translations';
import { Product, Category } from '../types';

export const CATEGORY_ARABIC_SLUGS: Record<string, string> = {
  'phone-cases': 'كفرات-هواتف',
  'canvas': 'لوحات-كانفاس',
  'photo-frames': 'إطارات-صور',
  'wood-crafts': 'تحف-خشبية',
  'keychains': 'ميداليات-مفاتيح',
  'mugs': 'أكواب-مطبوعة',
  'tshirts': 'تيشيرتات-مطبوعة',
  'hoodies': 'سترات-هودي',
  'stickers': 'ملصقات-ستيكرز',
  'hats': 'قبعات-وكابات',
};

export const ARABIC_SLUG_TO_CATEGORY: Record<string, string> = Object.entries(CATEGORY_ARABIC_SLUGS).reduce(
  (acc, [catId, arSlug]) => {
    acc[arSlug] = catId;
    return acc;
  },
  {} as Record<string, string>
);

export const getCategoryArabicSlug = (categoryId: string): string => {
  const norm = (categoryId || '').toLowerCase().trim();
  return CATEGORY_ARABIC_SLUGS[norm] || norm;
};

export const resolveCategoryIdFromSlug = (rawSlug: string, categories: Category[] = []): string | null => {
  if (!rawSlug) return null;
  let decoded = rawSlug;
  try {
    decoded = decodeURIComponent(rawSlug);
  } catch {}
  const clean = decoded.trim().toLowerCase();
  if (clean === 'all' || clean === 'الكل' || clean === 'جميع-الفئات') return 'all';
  if (ARABIC_SLUG_TO_CATEGORY[clean]) {
    return ARABIC_SLUG_TO_CATEGORY[clean];
  }
  if (CATEGORY_ARABIC_SLUGS[clean]) {
    return clean;
  }
  const found = categories.find(
    c =>
      c.id.toLowerCase() === clean ||
      (c.slug && c.slug.toLowerCase() === clean) ||
      getCategoryArabicSlug(c.id) === clean
  );
  return found ? found.id : null;
};

export const getCategoryLocalizedName = (category: string, language: Language): string => {
  const norm = (category || '').toLowerCase().trim();
  if (language !== 'ar') {
    switch (norm) {
      case 'phone-cases':
      case 'phone-case':
        return 'Phone Cases';
      case 'canvas':
        return 'Wall Art Canvas';
      case 'photo-frames':
      case 'photo-frame':
        return 'Photo Frames';
      case 'wood-crafts':
        return 'Wood Crafts';
      case 'keychains':
        return 'Custom Keychains';
      case 'mugs':
      case 'mug':
        return 'Photo Mugs';
      case 'tshirts':
      case 'tshirt':
        return 'T-Shirts & Apparel';
      case 'hoodies':
      case 'hoodie':
        return 'Hoodies';
      case 'stickers':
      case 'sticker':
        return 'Stickers';
      case 'hats':
      case 'hat':
        return 'Hats & Caps';
      case 'all':
        return 'All Categories';
      default:
        return category.replace(/-/g, ' ');
    }
  }

  switch (norm) {
    case 'phone-cases':
    case 'phone-case':
      return 'كفرات هواتف مخصصة';
    case 'canvas':
      return 'لوحات كانفاس جدارية';
    case 'photo-frames':
    case 'photo-frame':
      return 'إطارات صور فاخرة';
    case 'wood-crafts':
      return 'تحف ولوحات خشبية';
    case 'keychains':
      return 'ميداليات مفاتيح مخصصة';
    case 'mugs':
    case 'mug':
      return 'أكواب سيراميك مطبوعة';
    case 'tshirts':
    case 'tshirt':
      return 'قمصان وتيشيرتات مطبوعة';
    case 'hoodies':
    case 'hoodie':
      return 'سترات هودي شتوية';
    case 'stickers':
    case 'sticker':
      return 'ملصقات وستيكرز مقاومة للماء';
    case 'hats':
    case 'hat':
      return 'قبعات وكابات مطبوعة';
    case 'all':
      return 'كافة الفئات';
    default:
      return 'منتجات مطبوعة حسب الطلب';
  }
};

export const getCategorySingularArabicPrefix = (category: string): string => {
  const norm = (category || '').toLowerCase().trim();
  switch (norm) {
    case 'phone-cases':
    case 'phone-case':
      return 'كفر هاتف';
    case 'canvas':
      return 'لوحة كانفاس جدارية';
    case 'photo-frames':
    case 'photo-frame':
      return 'إطار صورة';
    case 'wood-crafts':
      return 'لوحة خشبية محفورة';
    case 'keychains':
      return 'ميدالية مفاتيح';
    case 'mugs':
    case 'mug':
      return 'كوب سيراميك مطبوع';
    case 'tshirts':
    case 'tshirt':
      return 'تيشيرت قطني مطبوع';
    case 'hoodies':
    case 'hoodie':
      return 'هودي شتوي مطبوع';
    case 'stickers':
    case 'sticker':
      return 'مجموعة ملصقات فينيل';
    case 'hats':
    case 'hat':
      return 'قبعة مطبوعة';
    default:
      return 'منتج مطبوع مخصص';
  }
};

// Multi-word and single-word dictionary for translating English product titles, tags, and themes into natural Arabic
const PHRASE_DICTIONARY: [RegExp, string][] = [
  [/\bcustom\s+printed\s+phone\s+case(s)?\b/gi, 'كفر هاتف مطبوع حسب الطلب'],
  [/\bcustom\s+phone\s+case(s)?\b/gi, 'كفر هاتف مخصص'],
  [/\bphone\s+case(s)?\b/gi, 'كفر هاتف'],
  [/\biphone\s+case(s)?\b/gi, 'كفر آيفون'],
  [/\bsamsung\s+case(s)?\b/gi, 'كفر سامسونج'],
  [/\bwall\s+art\s+canvas\b/gi, 'لوحة كانفاس جدارية'],
  [/\bcanvas\s+wall\s+art\b/gi, 'لوحة كانفاس جدارية'],
  [/\bstretched\s+canvas\b/gi, 'لوحة كانفاس مشدودة على خشب'],
  [/\bphoto\s+frame(s)?\b/gi, 'إطار صورة فاخر'],
  [/\bwood(en)?\s+craft(s)?\b/gi, 'تحفة خشبية محفورة'],
  [/\bceramic\s+mug(s)?\b/gi, 'كوب سيراميك'],
  [/\bcoffee\s+mug(s)?\b/gi, 'كوب قهوة'],
  [/\bdrinking\s+mug(s)?\b/gi, 'كوب مطبوع'],
  [/\bmagic\s+mug(s)?\b/gi, 'كوب سحري حراري'],
  [/\bt-shirt(s)?\b/gi, 'تيشيرت قطني'],
  [/\btshirt(s)?\b/gi, 'تيشيرت قطني'],
  [/\bgraphic\s+tee(s)?\b/gi, 'تيشيرت مطبوع'],
  [/\bheavyweight\s+hoodie(s)?\b/gi, 'هودي شتوي ثقيل'],
  [/\bfleece\s+hoodie(s)?\b/gi, 'هودي مبطن ناعم'],
  [/\bdie-cut\s+sticker(s)?\b/gi, 'ملصقات مفرغة'],
  [/\bvinyl\s+sticker(s)?\b/gi, 'ملصقات فينيل مقاومة للماء'],
  [/\bkey\s*chain(s)?\b/gi, 'ميدالية مفاتيح'],
  [/\bbaseball\s+cap(s)?\b/gi, 'قبعة كاب'],
  [/\bsnapback\s+cap(s)?\b/gi, 'قبعة سناب باك'],
  [/\bblack\s+matte\b/gi, 'أسود مطفي'],
  [/\bmatte\s+black\b/gi, 'أسود مطفي'],
  [/\btransparent\s+clear\b/gi, 'شفاف كريستال'],
  [/\bshock\s*proof\b/gi, 'مقاوم للصدمات'],
  [/\bprint\s+your\s+(own\s+)?(photo|image|design|picture)\b/gi, 'اطبع صورتك أو تصميمك الخاص'],
  [/\bcustom\s+(photo|image|design|print|art)\b/gi, 'طباعة صورتك وتصميمك'],
  [/\bpersonalized\s+gift(s)?\b/gi, 'هدية مخصصة'],
  [/\ball\s+phone\s+models\b/gi, 'لجميع موديلات الهواتف'],
  [/\ball\s+models\b/gi, 'لكافة الموديلات'],
  [/\bspitfire\b/gi, 'سبيتفاير'],
  [/\bspider[\s-]*man\b/gi, 'سبايدرمان'],
  [/\biron[\s-]*man\b/gi, 'آيرون مان'],
  [/\bbat[\s-]*man\b/gi, 'باتمان'],
  [/\bsuper[\s-]*man\b/gi, 'سوبرمان'],
  [/\bone\s+piece\b/gi, 'ون بيس'],
  [/\bdragon\s+ball\b/gi, 'دراغون بول'],
  [/\battack\s+on\s+titan\b/gi, 'هجوم العمالقة'],
  [/\bdemon\s+slayer\b/gi, 'قاتل الشياطين'],
  [/\bjujutsu\s+kaisen\b/gi, 'جوجوتسو كايسن'],
  [/\breal\s+madrid\b/gi, 'ريال مدريد'],
  [/\bfc\s+barcelona\b/gi, 'برشلونة'],
  [/\bmanchester\s+united\b/gi, 'مانشستر يونايتد'],
  [/\bmanchester\s+city\b/gi, 'مانشستر سيتي'],
  [/\bluis\s+vuitton\b/gi, 'لويس فيتون'],
  [/\blouis\s+vuitton\b/gi, 'لويس فيتون'],
  [/\bstreet\s*wear\b/gi, 'ستريت وير عصري'],
  [/\bcyber\s*punk\b/gi, 'سايبربانك'],
  [/\blimited\s+edition\b/gi, 'إصدار محدود'],
  [/\bbest\s*seller\b/gi, 'الأكثر مبيعاً'],
  [/\bnew\s+drop\b/gi, 'إصدار جديد'],
];

const WORD_DICTIONARY: Record<string, string> = {
  custom: 'مخصص',
  customized: 'مخصص',
  personalized: 'مخصص بالاسم أو الصورة',
  printed: 'مطبوع',
  print: 'طباعة',
  printing: 'طباعة',
  case: 'كفر',
  cases: 'كفرات',
  cover: 'كفر',
  covers: 'كفرات',
  phone: 'هاتف',
  mobile: 'موبايل',
  iphone: 'آيفون',
  samsung: 'سامسونج',
  galaxy: 'جالاكسي',
  xiaomi: 'شاومي',
  redmi: 'ريدمي',
  poco: 'بوكو',
  huawei: 'هواوي',
  honor: 'هونر',
  pixel: 'بيكسل',
  canvas: 'كانفاس',
  wall: 'جدارية',
  art: 'فنية',
  artwork: 'تصميم فني',
  frame: 'إطار',
  frames: 'إطارات',
  framed: 'مؤطر',
  photo: 'صورة',
  photos: 'صور',
  picture: 'صورة',
  image: 'صورة',
  wood: 'خشب طبيعي',
  wooden: 'خشبي',
  craft: 'تحفة',
  crafts: 'تحف',
  engraved: 'محفور بالليزر',
  laser: 'ليزر',
  plaque: 'لوحة',
  keychain: 'ميدالية مفاتيح',
  keychains: 'ميداليات مفاتيح',
  acrylic: 'أكريليك',
  metal: 'معدن',
  leather: 'جلد',
  mug: 'كوب',
  mugs: 'أكواب',
  ceramic: 'سيراميك',
  coffee: 'قهوة',
  magic: 'سحري',
  tshirt: 'تيشيرت',
  tshirts: 'تيشيرتات',
  shirt: 'قميص',
  tee: 'تيشيرت',
  cotton: 'قطن',
  heavyweight: 'ثقيل فاخر',
  oversized: 'أوفر سايز',
  hoodie: 'هودي',
  hoodies: 'هوديات',
  fleece: 'مبطن شتوي',
  sweatshirt: 'سويتشيرت',
  sticker: 'ستيكر',
  stickers: 'ستيكرز',
  vinyl: 'فينيل',
  waterproof: 'مقاوم للماء',
  pack: 'مجموعة',
  packs: 'مجموعات',
  hat: 'قبعة',
  hats: 'قبعات',
  cap: 'كاب',
  caps: 'كابات',
  snapback: 'سناب باك',
  black: 'أسود',
  white: 'أبيض',
  red: 'أحمر',
  blue: 'أزرق',
  green: 'أخضر',
  gold: 'ذهبي',
  golden: 'ذهبي',
  silver: 'فضي',
  pink: 'وردي',
  purple: 'بنفسجي',
  yellow: 'أصفر',
  orange: 'برتقالي',
  brown: 'بني',
  beige: 'بيج',
  grey: 'رمادي',
  gray: 'رمادي',
  clear: 'شفاف',
  transparent: 'شفاف',
  matte: 'مطفي',
  glossy: 'لامع',
  neon: 'نيون',
  dark: 'داكن',
  light: 'فاتح',
  retro: 'ريترو كلاسيك',
  vintage: 'كلاسيكي',
  modern: 'عصري',
  minimal: 'بسيط وأنيق',
  minimalist: 'بسيط وأنيق',
  luxury: 'فاخر',
  premium: 'ممتاز',
  aesthetic: 'جمالي',
  abstract: 'تجريدي',
  geometric: 'هندسي',
  floral: 'زهور',
  flower: 'زهرة',
  flowers: 'ورود',
  rose: 'وردة',
  marble: 'رخامي',
  nature: 'طبيعة',
  mountain: 'جبال',
  ocean: 'محيط',
  sea: 'بحر',
  wave: 'موج',
  sunset: 'غروب',
  sunrise: 'شروق',
  sky: 'سماء',
  moon: 'قمر',
  star: 'نجمة',
  stars: 'نجوم',
  space: 'فضاء',
  galaxy_theme: 'مجرة',
  anime: 'أنمي',
  manga: 'مانجا',
  naruto: 'ناروتو',
  luffy: 'لوفي',
  zoro: 'زورو',
  goku: 'غوكو',
  gojo: 'غوجو',
  itachi: 'إيتاشي',
  sasuke: 'ساسكي',
  kakashi: 'كاكاشي',
  tanjiro: 'تانجيرو',
  nezuko: 'نيزوكو',
  eren: 'إيرين',
  levi: 'ليفاي',
  marvel: 'مارفل',
  avengers: 'أفنجرز',
  joker: 'الجوكر',
  venom: 'فينوم',
  deadpool: 'ديدبول',
  disney: 'ديزني',
  stitch: 'ستيتش',
  mickey: 'ميكي',
  minnie: 'ميني',
  hellokitty: 'هيلو كيتي',
  kitty: 'كيتي',
  barbie: 'باربي',
  gaming: 'ألعاب جيمينج',
  gamer: 'جيمر',
  playstation: 'بلايستيشن',
  football: 'كرة قدم',
  soccer: 'كرة قدم',
  messi: 'ميسي',
  ronaldo: 'رونالدو',
  neymar: 'نيمار',
  mbappe: 'مبابي',
  barcelona: 'برشلونة',
  madrid: 'مدريد',
  liverpool: 'ليفربول',
  arsenal: 'أرسنال',
  chelsea: 'تشيلسي',
  milan: 'ميلان',
  bayern: 'بايرن',
  car: 'سيارة',
  cars: 'سيارات',
  bmw: 'بي إم دبليو',
  mercedes: 'مرسيدس',
  porsche: 'بورش',
  ferrari: 'فيراري',
  lamborghini: 'لامبورغيني',
  audi: 'أودي',
  nissan: 'نيسان',
  toyota: 'تويوتا',
  supra: 'سوبرا',
  gtr: 'جي تي آر',
  couple: 'ثنائي وعشاق',
  couples: 'ثنائي وعشاق',
  love: 'حب',
  heart: 'قلب',
  family: 'عائلة',
  wedding: 'زفاف',
  graduation: 'تخرج',
  birthday: 'عيد ميلاد',
  gift: 'هدية',
  gifts: 'هدايا',
  pet: 'حيوان أليف',
  cat: 'قطة',
  dog: 'كلب',
  lion: 'أسد',
  wolf: 'ذئب',
  butterfly: 'فراشة',
  dragon: 'تنين',
  calligraphy: 'خط عربي',
  arabic: 'عربي',
  islamic: 'إسلامي',
  quran: 'قرآني',
  verse: 'آية',
  beirut: 'بيروت',
  lebanon: 'لبنان',
  cedar: 'أرزة لبنان',
  music: 'موسيقى',
  spotify: 'سبوتيفاي',
  code: 'كود',
  name: 'اسم',
  letter: 'حرف',
  initial: 'حرف',
  quote: 'عبارة',
  typography: 'تصميم خطوط',
  graffiti: 'جرافيتي',
  street: 'ستريت',
  urban: 'عصري',
  tokyo: 'طوكيو',
  japan: 'اليابان',
  japanese: 'ياباني',
  edition: 'إصدار',
  collection: 'مجموعة',
  series: 'سلسلة',
  style: 'ستايل',
  design: 'تصميم',
  pattern: 'نقشة',
  collage: 'كولاج صور',
  portrait: 'بورتريه',
  pro: 'برو',
  max: 'ماكس',
  ultra: 'ألترا',
  plus: 'بلس',
  mini: 'ميني',
  and: 'و',
  with: 'مع',
  for: 'لـ',
  in: 'في',
  on: 'على',
  of: 'من',
  the: '',
  a: '',
  an: '',
};

/**
 * Phonetic transliterator for any remaining unknown English word so 100% of product titles
 * and slugs on /ar/ are rendered in Arabic script (never leaving raw Latin letters).
 */
const transliterateWordToArabic = (word: string): string => {
  const clean = word.trim();
  if (!clean) return '';
  // Keep pure numbers or dimension tokens like 40x60, 3D, A4, A3
  if (/^[0-9]+([x×*][0-9]+)?(cm|mm|oz)?$/i.test(clean)) {
    return clean.replace(/cm/i, 'سم').replace(/oz/i, 'أونصة');
  }
  if (/^(a4|a3|3d|2d|4k|hd|uv|dtf|s|m|l|xl|2xl|3xl)$/i.test(clean)) {
    return clean.toUpperCase();
  }

  let s = clean.toLowerCase();
  // Remove silent trailing 'e' on words longer than 3 chars
  if (s.length > 3 && s.endsWith('e') && !/[aeiou]{2}e$/.test(s)) {
    s = s.slice(0, -1);
  }

  const digraphs: [RegExp, string][] = [
    [/tion/g, 'شن'],
    [/sion/g, 'جن'],
    [/sh/g, 'ش'],
    [/ch/g, 'تش'],
    [/th/g, 'ث'],
    [/ph/g, 'ف'],
    [/kh/g, 'خ'],
    [/gh/g, 'غ'],
    [/qu/g, 'كو'],
    [/ck/g, 'ك'],
    [/oo/g, 'و'],
    [/ee/g, 'ي'],
    [/ea/g, 'ي'],
    [/ai/g, 'اي'],
    [/ay/g, 'اي'],
    [/ou/g, 'او'],
    [/ow/g, 'و'],
  ];

  for (const [rgx, rep] of digraphs) {
    s = s.replace(rgx, rep);
  }

  const charMap: Record<string, string> = {
    a: 'ا',
    b: 'ب',
    c: 'ك',
    d: 'د',
    e: 'ي',
    f: 'ف',
    g: 'ج',
    h: 'ه',
    i: 'ي',
    j: 'ج',
    k: 'ك',
    l: 'ل',
    m: 'م',
    n: 'ن',
    o: 'و',
    p: 'ب',
    q: 'ق',
    r: 'ر',
    s: 'س',
    t: 'ت',
    u: 'و',
    v: 'ف',
    w: 'و',
    x: 'كس',
    y: 'ي',
    z: 'ز',
  };

  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (/[\u0600-\u06FF0-9]/.test(ch)) {
      out += ch;
    } else if (charMap[ch]) {
      // Avoid duplicate consecutive alef/ya/waw
      const mapped = charMap[ch];
      if (out.endsWith(mapped) && (mapped === 'ا' || mapped === 'ي' || mapped === 'و')) {
        continue;
      }
      out += mapped;
    }
  }
  return out || 'مخصص';
};

export const translateEnglishTextToArabic = (text: string, category?: string): string => {
  if (!text || !text.trim()) {
    return category ? `${getCategorySingularArabicPrefix(category)} مخصص` : 'منتج مطبوع مخصص';
  }

  // If already predominantly Arabic with no Latin letters, return as-is
  if (/[\u0600-\u06FF]/.test(text) && !/[a-zA-Z]/.test(text)) {
    return text.trim();
  }

  let working = text.trim();

  // Strip internal SKU patterns like COV-PC-01 from display title
  working = working.replace(/\bCOV-[A-Z0-9-]+\b/gi, '').trim();

  // Apply multi-word phrase replacements first
  for (const [pattern, replacement] of PHRASE_DICTIONARY) {
    working = working.replace(pattern, ` ${replacement} `);
  }

  // Split remaining tokens and translate or transliterate every English token
  const tokens = working
    .replace(/[_/]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  const translatedTokens: string[] = [];
  for (const rawToken of tokens) {
    if (/^[-–—•|&+]+$/.test(rawToken)) {
      if (rawToken === '&' || rawToken === '+') {
        translatedTokens.push('و');
      } else {
        translatedTokens.push('—');
      }
      continue;
    }

    // If token already contains Arabic characters and no Latin characters
    if (/[\u0600-\u06FF]/.test(rawToken) && !/[a-zA-Z]/.test(rawToken)) {
      translatedTokens.push(rawToken);
      continue;
    }

    const stripped = rawToken.replace(/^[^\w\u0600-\u06FF]+|[^\w\u0600-\u06FF]+$/g, '');
    if (!stripped) continue;

    const lower = stripped.toLowerCase();
    if (lower in WORD_DICTIONARY) {
      const mapped = WORD_DICTIONARY[lower];
      if (mapped) translatedTokens.push(mapped);
      continue;
    }

    // Handle hyphenated English words like "spider-man" or "custom-made"
    if (stripped.includes('-')) {
      const parts = stripped
        .split('-')
        .map(p => {
          const pl = p.toLowerCase();
          if (pl in WORD_DICTIONARY) return WORD_DICTIONARY[pl];
          return transliterateWordToArabic(p);
        })
        .filter(Boolean);
      if (parts.length > 0) {
        translatedTokens.push(parts.join(' '));
      }
      continue;
    }

    translatedTokens.push(transliterateWordToArabic(stripped));
  }

  // Deduplicate adjacent identical Arabic words
  const deduped: string[] = [];
  for (const tok of translatedTokens) {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== tok) {
      deduped.push(tok);
    }
  }

  let result = deduped.join(' ').replace(/\s+/g, ' ').trim();

  // Ensure the Arabic title starts with or includes the Arabic category descriptor for clarity & SEO
  if (category) {
    const prefix = getCategorySingularArabicPrefix(category);
    const firstKeyword = prefix.split(' ')[0];
    if (!result.includes(firstKeyword)) {
      result = `${prefix} ${result}`.trim();
    }
  }

  return result || (category ? `${getCategorySingularArabicPrefix(category)} مخصص` : 'منتج مطبوع مخصص');
};

export const getProductLocalizedTitle = (product: Product, language: Language): string => {
  if (language !== 'ar') {
    return product.title;
  }
  if (product.titleAr && product.titleAr.trim() && !/[a-zA-Z]/.test(product.titleAr)) {
    return product.titleAr.trim();
  }
  return translateEnglishTextToArabic(product.titleAr || product.title, product.category);
};

export const getProductLocalizedDescription = (product: Product, language: Language): string => {
  if (language !== 'ar') {
    return product.description;
  }
  if (product.descriptionAr && product.descriptionAr.trim() && !/[a-zA-Z]/.test(product.descriptionAr)) {
    return product.descriptionAr.trim();
  }
  if (/[\u0600-\u06FF]/.test(product.description) && !/[a-zA-Z]/.test(product.description)) {
    return product.description;
  }

  const arTitle = getProductLocalizedTitle(product, 'ar');
  const normCat = (product.category || '').toLowerCase();

  switch (normCat) {
    case 'phone-cases':
      return `${arTitle} من استوديو كوفريستيك في لبنان. مصمم بخامات متينة مقاومة للصدمات والخدوش مع حواف مرتفعة لحماية الكاميرا والشاشة، ومطبوع بدقة عالية وألوان ثابتة لا تتقشر. متوفر لجميع موديلات آيفون وسامسونج وشاومي وهواوي مع إمكانية طباعة صورتك أو تصميمك الخاص ومعاينة البروفة مجاناً عبر واتساب قبل الطباعة والتوصيل السريع لكافة المناطق اللبنانية.`;
    case 'canvas':
      return `${arTitle} مطبوعة بأحبار أصلية عالية الدقة على قماش كانفاس فاخر ومشدودة بإتقان على إطار خشبي طبيعي متين جاهز للتعليق الفوري. مثالية لتخليد صور العائلة والزفاف والتصاميم الفنية مع معاينة مجانية عبر واتساب قبل الطباعة وتوصيل سريع في كافة أنحاء لبنان.`;
    case 'photo-frames':
      return `${arTitle} بتصميم أنيق مع طبقة حماية كريستالية تحافظ على نقاء الألوان لسنوات طويلة. مناسب للمكاتب وغرف المعيشة والهدايا التذكارية في لبنان مع خدمة معاينة التصميم عبر واتساب والدفع عند الاستلام.`;
    case 'wood-crafts':
      return `${arTitle} منفذة بحفر ليزر عالي الدقة وطباعة مخصصة على الخشب الطبيعي الفاخر. هدية تذكارية مميزة للمناسبات الخاصة والمكاتب في لبنان مع معاينة مجانية عبر واتساب قبل التنفيذ.`;
    case 'keychains':
      return `${arTitle} مطبوعة من الوجهين بدقة عالية ومقاومة للخدش. احتفظ بصورك المفضلة أو كود أغنيتك أو اسمك في ميدالية أنيقة وعملية مع توصيل سريع في جميع المناطق اللبنانية.`;
    case 'mugs':
      return `${arTitle} بطباعة حرارية عالية الوضوح والثبات وآمنة للاستخدام اليومي. يمكنك تخصيص الكوب بصورتك أو عبارتك المفضلة أو شعار عملك مع معاينة البروفة عبر واتساب قبل الطباعة.`;
    case 'tshirts':
      return `${arTitle} مصنوع من قطن ممتاز ناعم ومريح بوزن ثقيل مع طباعة ديجيتال مرنة وثابتة لا تتأثر بالغسيل. متوفر بكافة المقاسات في لبنان مع إمكانية طباعة تصميمك الخاص من الأمام أو الخلف.`;
    case 'hoodies':
      return `${arTitle} بقماش شتوي مبطن دافئ ومريح مع طباعة عالية الدقة والثبات. خيار مثالي للإطلالة العصرية والهدايا المخصصة في لبنان مع توصيل سريع والدفع نقداً عند الاستلام.`;
    case 'stickers':
      return `${arTitle} مقصوصة بدقة ومغلفة بطبقة حماية مقاومة للماء والشمس والخدوش. مثالية للابتوب والهاتف والسيارات والدفاتر مع إمكانية طباعة أي شعار أو صورة خاصة.`;
    case 'hats':
      return `${arTitle} بتصميم عصري مريح وقابل للتعديل مع طباعة أمامية متقنة. خصص قبعتك بشعارك أو عبارتك المفضلة مع توصيل سريع إلى كافة المناطق اللبنانية.`;
    default:
      return `${arTitle} مطبوع حسب الطلب بأعلى معايير الجودة في استوديو كوفريستيك لبنان. متوفر مع خدمة معاينة البروفة المجانية عبر واتساب قبل الطباعة والتوصيل السريع خلال ٢٤ إلى ٤٨ ساعة مع الدفع عند الاستلام.`;
  }
};

export const getProductLocalizedDetails = (product: Product, language: Language): string[] => {
  if (language !== 'ar') {
    return product.details || [];
  }
  if (Array.isArray(product.detailsAr) && product.detailsAr.length > 0) {
    return product.detailsAr;
  }

  const normCat = (product.category || '').toLowerCase();
  switch (normCat) {
    case 'phone-cases':
      return [
        'خامة متينة مقاومة للصدمات مع حواف بارزة لحماية الكاميرا والشاشة',
        'طباعة عالية الدقة بوضوح ١٢٠٠ نقطة في البوصة مقاومة للخدش والتقشر',
        'متوفر بخامة شفافة كريستال أو أسود مطفي فاخر لكافة موديلات الهواتف',
        'معاينة بروفة التصميم مجاناً عبر واتساب قبل الطباعة والتوصيل في لبنان'
      ];
    case 'canvas':
      return [
        'قماش كانفاس فاخر مطبوع بأحبار أصلية مقاومة للبهتان',
        'مشدود يدوياً على إطار خشب طبيعي متين وجاهز للتعليق مباشرة',
        'متوفر بمقاسات متعددة تناسب غرف المعيشة والمكاتب',
        'تغليف آمن وتوصيل سريع لكافة المحافظات اللبنانية مع الدفع عند الاستلام'
      ];
    case 'mugs':
      return [
        'سيراميك عالي الجودة بسطح أملس وطباعة حرارية زاهية الألوان',
        'متوفر بحجم ١١ أونصة و١٥ أونصة وخيار الكوب السحري الحراري',
        'مقاوم للبهتان وآمن للاستخدام اليومي',
        'إمكانية الطباعة على الجانبين مع معاينة مجانية عبر واتساب'
      ];
    case 'tshirts':
    case 'hoodies':
      return [
        'قماش قطني ثقيل وناعم يمنحك راحة فائقة طوال اليوم',
        'طباعة ديجيتال مرنة وعالية الثبات لا تتشقق مع الغسيل',
        'متوفر بجميع المقاسات من الصغير حتى ٣ إكس لارج',
        'إمكانية تخصيص الطباعة الأمامية أو الخلفية حسب طلبك'
      ];
    default:
      return [
        'طباعة مخصصة عالية الدقة بألوان زاهية وثبات طويل الأمد',
        'فحص الجودة وإرسال بروفة معاينة عبر واتساب قبل التنفيذ',
        'توصيل سريع خلال ٢٤ إلى ٤٨ ساعة في جميع المناطق اللبنانية',
        'الدفع نقداً عند الاستلام بالدولار أو عبر ويش موني'
      ];
  }
};

export const getProductLocalizedBadge = (badge: string | undefined, language: Language): string | undefined => {
  if (!badge) return undefined;
  if (language !== 'ar') return badge;
  const upper = badge.trim().toUpperCase();
  const map: Record<string, string> = {
    'NEW DROP': 'إصدار جديد',
    'NEW': 'جديد',
    'BESTSELLER': 'الأكثر مبيعاً',
    'BEST SELLER': 'الأكثر مبيعاً',
    'POPULAR': 'الأكثر طلباً',
    'LIMITED': 'إصدار محدود',
    'HEAVYWEIGHT': 'خامة ثقيلة فاخرة',
    'TRENDING': 'رائج الآن',
    'STAFF PICK': 'اختيار الاستوديو',
    'HOT': 'مميز',
    'PACKS': 'مجموعة موفرة',
    'CUSTOM': 'طباعة مخصصة',
    'SALE': 'عرض خاص',
    'OFFER': 'عرض خاص',
    'HANDCRAFTED': 'صناعة متقنة',
    'PERFECT GIFT': 'هدية مثالية',
    'BEST VALUE': 'أفضل قيمة',
    'WARM & COZY': 'شتوي دافئ',
  };
  return map[upper] || translateEnglishTextToArabic(badge);
};

export const getProductLocalizedTag = (tagText: string, language: Language): string => {
  if (!tagText) return '';
  if (language !== 'ar') return tagText;
  return translateEnglishTextToArabic(tagText);
};

export const getVariantLocalizedName = (variantName: string, language: Language): string => {
  if (language !== 'ar') return variantName;
  const lower = (variantName || '').toLowerCase();
  if (lower.includes('size')) return 'المقاس';
  if (lower.includes('dimension')) return 'مقاس وأبعاد اللوحة';
  if (lower.includes('color')) return 'اللون';
  if (lower.includes('frame')) return 'نوع ولون الإطار';
  if (lower.includes('finish')) return 'نوع خامة الكفر';
  if (lower.includes('model') || lower.includes('device')) return 'موديل الهاتف';
  if (lower.includes('material')) return 'الخامة والقماش';
  if (lower.includes('type') || lower.includes('style')) return 'النوع والتصميم';
  return translateEnglishTextToArabic(variantName);
};

export const getVariantOptionLocalizedName = (option: string, language: Language): string => {
  if (!option || language !== 'ar') return option;
  const lower = option.trim().toLowerCase();
  const exactMap: Record<string, string> = {
    'transparent': 'شفاف مقاوم للصدمات',
    'clear': 'شفاف كريستال',
    'black matte': 'أسود مطفي فاخر',
    'matte black': 'أسود مطفي فاخر',
    'all phone models': 'جميع موديلات الهواتف',
    'small': 'صغير (S)',
    'medium': 'وسط (M)',
    'large': 'كبير (L)',
    'x-large': 'كبير جداً (XL)',
    'xx-large': '٢ إكس لارج (2XL)',
    'black frame': 'إطار أسود أنيق',
    'white frame': 'إطار أبيض عصري',
    'wood frame': 'إطار خشبي طبيعي',
    'oak frame': 'إطار خشب بلوط',
    'gold frame': 'إطار ذهبي',
    '11oz classic white': '١١ أونصة أبيض كلاسيكي',
    '15oz large white': '١٥ أونصة حجم كبير',
    'magic heat-changing': 'كوب سحري حراري متغير اللون',
    'single': 'قطعة واحدة',
    'pack of 5': 'مجموعة ٥ قطع',
    'pack of 10': 'مجموعة ١٠ قطع',
  };
  if (exactMap[lower]) return exactMap[lower];
  return option
    .replace(/\bcm\b/gi, 'سم')
    .replace(/\boz\b/gi, 'أونصة');
};

/**
 * Generates an Arabic URL slug for a product so all old and new products
 * have authentic Arabic permalinks under /ar/product/<arabic-slug>
 */
export const slugifyArabic = (text: string): string => {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^\u0600-\u06FFa-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

export const getProductArabicSlug = (product: Product): string => {
  if (product.slugAr && product.slugAr.trim()) {
    return slugifyArabic(product.slugAr);
  }
  const arTitle = getProductLocalizedTitle(product, 'ar');
  const baseArSlug = slugifyArabic(arTitle) || 'منتج-مطبوع';
  // Extract numeric part from product.id (e.g. "COV-PC-01" -> "01") so permalink stays 100% Arabic + number
  const numMatch = (product.id || '').match(/\d+/);
  const suffix = numMatch ? numMatch[0] : slugifyArabic(translateEnglishTextToArabic(product.id || '1'));
  if (baseArSlug.endsWith(`-${suffix}`)) {
    return baseArSlug;
  }
  return `${baseArSlug}-${suffix}`;
};

export const getProductPermalink = (product: Product, language: Language): string => {
  if (language === 'ar') {
    return `/ar/product/${getProductArabicSlug(product)}`;
  }
  return `/product/${product.slug || product.id}`;
};

export const getCategoryPermalink = (categorySlug: string, language: Language): string => {
  const clean = (categorySlug || 'all').toLowerCase().trim();
  if (clean === 'all') {
    return language === 'ar' ? '/ar/shop' : '/shop';
  }
  if (language === 'ar') {
    return `/ar/category/${getCategoryArabicSlug(clean)}`;
  }
  return `/category/${clean}`;
};

export const getLocalizedPath = (path: string, language: Language): string => {
  const clean = ('/' + (path || '/').replace(/^\/+/, '').replace(/^ar(\/|$)/i, '')).replace(/\/+$/, '') || '/';
  if (language !== 'ar') {
    if (clean.startsWith('/category/')) {
      const catPart = clean.replace(/^\/category\//, '');
      const resolved = resolveCategoryIdFromSlug(catPart) || catPart;
      return `/category/${resolved}`;
    }
    return clean;
  }

  if (clean === '/' || clean === '/home') {
    return '/ar/';
  }
  if (clean.startsWith('/category/')) {
    const catPart = clean.replace(/^\/category\//, '');
    const resolved = resolveCategoryIdFromSlug(catPart) || catPart;
    return `/ar/category/${getCategoryArabicSlug(resolved)}`;
  }
  return `/ar${clean}`;
};

/**
 * Matches a URL product identifier (English slug, ID, or Arabic permalink slug)
 * against the product catalog.
 */
export const matchProductByIdentifier = (products: Product[], rawIdentifier: string): Product | undefined => {
  if (!rawIdentifier || !Array.isArray(products) || products.length === 0) return undefined;
  let decoded = rawIdentifier;
  try {
    decoded = decodeURIComponent(rawIdentifier);
  } catch {}
  const cleanId = decoded.trim().toLowerCase().replace(/\/+$/, '');

  // 1. Direct match on English slug, ID, or Arabic slug
  const exact = products.find(p => {
    const enSlug = (p.slug || '').toLowerCase();
    const idLower = (p.id || '').toLowerCase();
    const arSlug = getProductArabicSlug(p).toLowerCase();
    const customArSlug = (p.slugAr || '').toLowerCase();
    const titleArSlug = slugifyArabic(getProductLocalizedTitle(p, 'ar')).toLowerCase();
    return (
      enSlug === cleanId ||
      idLower === cleanId ||
      arSlug === cleanId ||
      (customArSlug && customArSlug === cleanId) ||
      titleArSlug === cleanId
    );
  });
  if (exact) return exact;

  // 2. Match if Arabic slug starts with titleArSlug or shares unique numeric suffix
  return products.find(p => {
    const arSlug = getProductArabicSlug(p).toLowerCase();
    const titleArSlug = slugifyArabic(getProductLocalizedTitle(p, 'ar')).toLowerCase();
    return (
      (titleArSlug && cleanId.startsWith(titleArSlug)) ||
      (arSlug && cleanId.includes(arSlug)) ||
      cleanId.endsWith(`-${(p.id || '').toLowerCase()}`)
    );
  });
};

/**
 * Enriches a product object (old or newly created) with computed Arabic fields
 * so that Arabic title, Arabic permalink slug, Arabic description, and Arabic details are always populated.
 */
export const enrichProductWithArabic = <T extends Product>(product: T): T => {
  const titleAr = product.titleAr && product.titleAr.trim()
    ? product.titleAr.trim()
    : getProductLocalizedTitle(product, 'ar');
  const tempProduct = { ...product, titleAr };
  const slugAr = product.slugAr && product.slugAr.trim()
    ? slugifyArabic(product.slugAr)
    : getProductArabicSlug(tempProduct);
  const descriptionAr = product.descriptionAr && product.descriptionAr.trim()
    ? product.descriptionAr.trim()
    : getProductLocalizedDescription(tempProduct, 'ar');
  const detailsAr = Array.isArray(product.detailsAr) && product.detailsAr.length > 0
    ? product.detailsAr
    : getProductLocalizedDetails(tempProduct, 'ar');

  return {
    ...product,
    titleAr,
    slugAr,
    descriptionAr,
    detailsAr,
  };
};
